import { getUsersByWorkEngagement } from "@/app/dashboard/technical-management/utils";
import type { FilterObject, JsonFilter } from "@/hooks/use-table-filter/types";
import { ObjectEntries } from "@/lib/utils";

type RewriteResult = {
  key: string;
  value: FilterObject;
}

/**
 * Filter rewrites are definitions of filters that are only supported in frontend
 * and need to be converted to another filter that is supported in backend.
 * For example, `accepted_by_work_engagement` will fetch users in the work engagement
 * and change the filter to `users_accepted_delegations`.
 */
type FilterRewriteRule = {
  key: string;
  rewrite: (filter: FilterObject) => Promise<RewriteResult | null> | RewriteResult | null;
}

const filterRewrites = [
  {
    key: "unanswered_by_work_engagement",
    rewrite: async (filter: FilterObject) => {
      const workEngagementId = Array.isArray(filter.noop) ? filter.noop.at(0) : null;
      if (!workEngagementId) {
        return null
      }

      const userIds = await getUsersByWorkEngagement(workEngagementId, { queryParams: { pageSize: 30 } })

      return {
        key: "users_unanswered_delegations",
        value: {
          noop: userIds
        }
      }
    }
  },
  {
    key: 'accepted_by_work_engagement',
    rewrite: async (filter: FilterObject) => {

      const workEngagementId = Array.isArray(filter.noop) ? filter.noop.at(0) : null;
      if (!workEngagementId) {
        return null
      }

      // Fetch a maximum of 30 because more could make the url too long.
      const userIds = await getUsersByWorkEngagement(workEngagementId, { queryParams: { pageSize: 30 } })

      return {
        key: "users_accepted_delegations",
        value: {
          noop: userIds
        }
      }
    }
  }
] as const satisfies FilterRewriteRule[];

export async function rewriteFilter(jsonFilter: JsonFilter | undefined) {
  if (!jsonFilter) {
    return undefined;
  }

  const filterEntries = ObjectEntries(jsonFilter);

  const rewritePromises = filterEntries.map(async ([key, value]) => {
    if (!value) {
      return null;
    }

    const rewriteRule = filterRewrites.find((rewrite) => rewrite.key === key);

    if (rewriteRule) {
      try {
        const rewritten = await rewriteRule.rewrite(value);
        if (!rewritten) {
          return null;
        }
        return [rewritten.key, rewritten.value] as const;
      } catch {
        console.error(`Failed to rewrite filter with key "${key}"`);
        return null;
      }
    }
    return [key, value] as const;
  })

  const rewrittenEntries = await Promise.all(rewritePromises);

  return Object.fromEntries(rewrittenEntries.filter(Boolean)) as JsonFilter;
}